/**
 * Hook that powers the Properties page.
 *
 * A property is "owned" — and shown here — when `pipeline_properties.is_owned`
 * is true. Its acquisition date is `owned_date`, falling back to the row's
 * `created_at` when `owned_date` was never recorded, so ownership no longer
 * hinges on that column being populated. Ownership is explicit and independent
 * of rehab (rehab data only enriches status + the cash-flow phase window).
 *
 * Each entry batches the data the card + KPI row need:
 *   - `pipeline_properties` row (property_data, strategy, purchase_price, owned_date)
 *   - `property_setting`        row (deal inputs + strategy blobs + rehab)
 *   - `rehab_projects`          row (start_date, actual_completion, stage) — optional
 *   - `rehab_line_items`        rows (actual_cost + status → actual rehab spend)
 *   - `loans`                   row (financing metadata)
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import type { DealSettingsRow } from "@/hooks/use-deal-settings";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  composeStrategySettings,
  type RawStrategyColumns,
} from "@/lib/property-setting";
import type { LoanMeta } from "@/components/financing/financing-metrics";
import type { RehabActualRow } from "@/components/properties/property-metrics";
import { MockProperty } from "@/data/mockProperties";
import type { Strategy } from "@/types/pipeline";

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;

interface RehabRow {
  pipeline_property_id: string;
  start_date: string | null;
  actual_completion: string | null;
  stage: string;
}

interface RentPaymentRow {
  pipeline_property_id: string;
  paid_date: string | null;
  amount: number | null;
}

/** `rehab_line_items` columns needed to total recognized rehab spend. */
interface RehabLineItemCostRow extends RehabActualRow {
  pipeline_property_id: string | null;
}

/** Latest month's collected rent for a property — the value of its most recent
 * "YYYY-MM" bucket (keys sort lexicographically = chronologically). */
function latestMonthRent(byMonth: Map<string, number> | undefined): number | null {
  if (!byMonth || byMonth.size === 0) return null;
  let latestYm = "";
  for (const ym of byMonth.keys()) if (ym > latestYm) latestYm = ym;
  return byMonth.get(latestYm) ?? null;
}

interface PipelinePropertyRow {
  id: string;
  pipeline_id: string | null;
  property_data: unknown;
  purchase_price: number | null;
  strategy: Strategy | null;
  notes: string | null;
  is_owned: boolean | null;
  owned_date: string | null;
  /** Disposition — true once the property has been sold. */
  is_closed: boolean | null;
  /** "YYYY-MM-DD" sale (closing) date. */
  closed_at: string | null;
  /** Actual sell-side costs in dollars, captured at sale. */
  closing_costs: number | null;
  /** Row creation timestamp — acquisition-date fallback when owned_date is null. */
  created_at: string;
}

export interface OwnedProperty {
  /** pipeline_properties.id — also the React key. */
  id: string;
  property: MockProperty;
  strategy: Strategy | null;
  purchasePrice: number | null;
  /**
   * Acquisition date (cash-flow lifecycle start) — `pipeline_properties.owned_date`,
   * or the row's `created_at` day when owned_date is null. Effectively never null.
   */
  ownedDate: string | null;
  /** `rehab_projects.start_date` — when rehab began (phase window only). */
  rehabStartDate: string | null;
  /** `rehab_projects.actual_completion` — rehab end (operating boundary; null while in rehab). */
  movedAt: string | null;
  /** Full `property_setting` row, or null if none exists yet. */
  settings: DealSettingsRow | null;
  /** Financing metadata from `loans` (lender / enabled / slot), or null. */
  loan: LoanMeta | null;
  /**
   * `rehab_line_items` rows for the property. Only the completed ones count
   * toward rehab spend — see `computePropertyMetrics`. Empty when the property
   * has no scope yet.
   */
  rehabLineItems: RehabActualRow[];
  /** "in_rehab" | "owned" | "sold" — derived from data. */
  status: "in_rehab" | "owned" | "sold";
  /** "YYYY-MM-DD" sale date when sold, else null. */
  closedDate: string | null;
  /** Actual sell-side costs ($) captured at sale, else null. */
  closingCosts: number | null;
  /**
   * True when a `rehab_projects` row exists for this property (any stage).
   * Gates the card's "Move to Rehab" action — only offered when false.
   */
  hasRehabProject: boolean;
  /**
   * Rent collected in the latest month with a paid `rent_payments` record, or
   * null if none exists yet. Drives the card + KPI cash-flow metrics off actual
   * income; null callers fall back to the projected `monthly_rent`.
   */
  actualMonthlyRent: number | null;
}

interface LoanMetaRow extends LoanMeta {
  pipeline_property_id: string;
}

export function useOwnedProperties() {
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;
  const enabled = Boolean(workspaceId);

  // 1. Owned properties — explicit is_owned on pipeline_properties. owned_date
  //    is the acquisition date, but a missing one no longer excludes the row
  //    (we fall back to created_at below).
  //    (is_owned / owned_date aren't in the generated types yet — structural cast.)
  const ownedQuery = useQuery({
    queryKey: ["pipeline_properties_owned", workspaceId] as const,
    enabled,
    queryFn: async () => {
      if (!workspaceId) return [] as PipelinePropertyRow[];
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (c: string, v: string) => DbResp<PipelinePropertyRow[]>;
          };
        };
      };
      const { data, error } = (await client
        .from("pipeline_properties")
        .select("*")
        .eq("workspace_id", workspaceId)) as PostgrestSingleResponse<PipelinePropertyRow[]>;
      if (error) throw error;
      return (data ?? []).filter((p) => p.is_owned === true);
    },
  });

  const ownedRows = useMemo(() => ownedQuery.data ?? [], [ownedQuery.data]);
  const ownedIds = useMemo(() => ownedRows.map((p) => p.id), [ownedRows]);

  // 2. Rehab projects (status + cash-flow phase window) for owned ids.
  const rehabQuery = useQuery({
    queryKey: ["rehab_projects_owned", workspaceId, ownedIds] as const,
    enabled: enabled && ownedIds.length > 0,
    queryFn: async () => {
      if (!workspaceId || ownedIds.length === 0) return [] as RehabRow[];
      const { data, error } = (await supabase
        .from("rehab_projects")
        .select("pipeline_property_id, start_date, actual_completion, stage")
        .eq("workspace_id", workspaceId)
        .in("pipeline_property_id", ownedIds)) as PostgrestSingleResponse<RehabRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // 3. property_setting rows for the owned set.
  const settingsQuery = useQuery({
    queryKey: ["property_settings_owned", workspaceId, ownedIds] as const,
    enabled: enabled && ownedIds.length > 0,
    queryFn: async () => {
      if (!workspaceId || ownedIds.length === 0) return [] as DealSettingsRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (
                col: string,
                v: string,
              ) => { in: (col: string, v: string[]) => DbResp<DealSettingsRow[]> };
            };
          };
        }
      )
        .from("property_setting")
        .select("*")
        .eq("workspace_id", workspaceId)
        .in("pipeline_property_id", ownedIds)) as PostgrestSingleResponse<DealSettingsRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // 4. Financing metadata (lender / enabled / slot) from the loans table.
  const loansQuery = useQuery({
    queryKey: ["loans_owned", workspaceId, ownedIds] as const,
    enabled: enabled && ownedIds.length > 0,
    queryFn: async () => {
      if (!workspaceId || ownedIds.length === 0) return [] as LoanMetaRow[];
      const { data, error } = (await supabase
        .from("loans")
        .select("pipeline_property_id, loan_type, lender_name, is_enabled")
        .eq("workspace_id", workspaceId)
        .in("pipeline_property_id", ownedIds)) as PostgrestSingleResponse<LoanMetaRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // 5. Actual rent collected (status = paid) for the owned set, bucketed by
  //    month. Strict-actual income — no projection.
  const rentQuery = useQuery({
    queryKey: ["rent_payments_owned", workspaceId, ownedIds] as const,
    enabled: enabled && ownedIds.length > 0,
    queryFn: async () => {
      if (!workspaceId || ownedIds.length === 0) return [] as RentPaymentRow[];
      const { data, error } = (await supabase
        .from("rent_payments")
        .select("pipeline_property_id, paid_date, amount")
        .eq("workspace_id", workspaceId)
        .in("pipeline_property_id", ownedIds)
        .eq("status", "paid")
        .not("paid_date", "is", null)) as PostgrestSingleResponse<RentPaymentRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // 6. Rehab scope line items — the ONLY rehab-cost source for the card
  //    metrics (completed rows' actual_cost, see `computePropertyMetrics`).
  //    (pipeline_property_id isn't in the generated types yet — structural cast.)
  const lineItemsQuery = useQuery({
    queryKey: ["rehab_line_items_owned", workspaceId, ownedIds] as const,
    enabled: enabled && ownedIds.length > 0,
    queryFn: async () => {
      if (!workspaceId || ownedIds.length === 0) return [] as RehabLineItemCostRow[];
      const { data, error } = (await (
        supabase as unknown as {
          from: (t: string) => {
            select: (s: string) => {
              eq: (
                col: string,
                v: string,
              ) => { in: (col: string, v: string[]) => DbResp<RehabLineItemCostRow[]> };
            };
          };
        }
      )
        .from("rehab_line_items")
        .select("pipeline_property_id, actual_cost, status")
        .eq("workspace_id", workspaceId)
        .in(
          "pipeline_property_id",
          ownedIds,
        )) as PostgrestSingleResponse<RehabLineItemCostRow[]>;
      if (error) throw error;
      return data ?? [];
    },
  });

  // Per-property paid rent, summed into "YYYY-MM" buckets. Reused for the card
  // metrics (latest month) and the performance chart (per-month overlay).
  const rentByMonthByProperty = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const r of rentQuery.data ?? []) {
      if (!r.paid_date) continue;
      const ym = r.paid_date.slice(0, 7);
      let byMonth = map.get(r.pipeline_property_id);
      if (!byMonth) {
        byMonth = new Map<string, number>();
        map.set(r.pipeline_property_id, byMonth);
      }
      byMonth.set(ym, (byMonth.get(ym) ?? 0) + (Number(r.amount) || 0));
    }
    return map;
  }, [rentQuery.data]);

  // Rehab line items grouped by property — one bucket per owned id.
  const lineItemsByProperty = useMemo(() => {
    const map = new Map<string, RehabActualRow[]>();
    for (const r of lineItemsQuery.data ?? []) {
      if (!r.pipeline_property_id) continue;
      const bucket = map.get(r.pipeline_property_id);
      const entry = { actual_cost: r.actual_cost, status: r.status };
      if (bucket) bucket.push(entry);
      else map.set(r.pipeline_property_id, [entry]);
    }
    return map;
  }, [lineItemsQuery.data]);

  const items = useMemo<OwnedProperty[]>(() => {
    const rehabById = new Map((rehabQuery.data ?? []).map((r) => [r.pipeline_property_id, r]));
    // One financing row per property; prefer the enabled one if several exist.
    const loanById = new Map<string, LoanMeta>();
    for (const l of loansQuery.data ?? []) {
      const existing = loanById.get(l.pipeline_property_id);
      if (!existing || l.is_enabled) loanById.set(l.pipeline_property_id, l);
    }
    // Compose split strategy columns back into the nested flip/brrrr/rental shape.
    const settingsById = new Map(
      (settingsQuery.data ?? []).map((s) => {
        const { flip, brrrr, rental } = composeStrategySettings(
          s as unknown as RawStrategyColumns,
        );
        return [s.pipeline_property_id, { ...s, flip, brrrr, rental }];
      }),
    );

    return ownedRows
      .map((p) => {
        const r = rehabById.get(p.id);
        const propertyData = (p.property_data ?? {}) as Partial<MockProperty>;
        // Disposition reads from is_closed alone.
        //
        // This used to fall back to `property_data.status === "sold"`, which is
        // no longer safe: that field is the MARKET listing status from the feed,
        // and once we buy a property its MLS listing legitimately goes to
        // CLOSED. Re-syncing property_data would then make our own purchase look
        // like a disposal. 20260716000000_pipeline_properties_closed.sql already
        // backfilled is_closed from that flag, so nothing is lost by dropping it.
        const sold = p.is_closed === true;
        // Owned by default; still "In Rehab" when an unfinished rehab project exists.
        const status: OwnedProperty["status"] = sold
          ? "sold"
          : r && r.stage !== "completed"
            ? "in_rehab"
            : "owned";
        return {
          id: p.id,
          property: propertyData as MockProperty,
          strategy: p.strategy,
          purchasePrice: p.purchase_price,
          // Fall back to the row's creation date when owned_date was never set,
          // so the acquisition date (and every cash-flow lifecycle boundary
          // derived from it) is always available. created_at is a timestamp;
          // its leading YYYY-MM-DD is the calendar day we want.
          ownedDate: p.owned_date ?? p.created_at.slice(0, 10),
          rehabStartDate: r?.start_date ?? null,
          movedAt: r?.actual_completion ?? null,
          settings: settingsById.get(p.id) ?? null,
          loan: loanById.get(p.id) ?? null,
          rehabLineItems: lineItemsByProperty.get(p.id) ?? [],
          status,
          closedDate: p.closed_at ?? null,
          closingCosts: p.closing_costs ?? null,
          hasRehabProject: rehabById.has(p.id),
          actualMonthlyRent: latestMonthRent(rentByMonthByProperty.get(p.id)),
        };
      })
      // Most recently owned first.
      .sort((a, b) => (b.ownedDate ?? "").localeCompare(a.ownedDate ?? ""));
  }, [
    ownedRows,
    rehabQuery.data,
    settingsQuery.data,
    loansQuery.data,
    lineItemsByProperty,
    rentByMonthByProperty,
  ]);

  return {
    items,
    /** Per-property paid rent, summed by "YYYY-MM". Powers the chart overlay. */
    rentByMonthByProperty,
    isLoading:
      ownedQuery.isLoading ||
      (ownedIds.length > 0 &&
        (rehabQuery.isLoading ||
          settingsQuery.isLoading ||
          loansQuery.isLoading ||
          lineItemsQuery.isLoading ||
          rentQuery.isLoading)),
    refetch: async () => {
      await Promise.all([
        ownedQuery.refetch(),
        rehabQuery.refetch(),
        settingsQuery.refetch(),
        loansQuery.refetch(),
        lineItemsQuery.refetch(),
        rentQuery.refetch(),
      ]);
    },
  };
}
